/** Store → Customers list: search, tag filter, sort, bulk tags, add customer. */
import { ExportCustomersButton } from './ShopExports';
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Checkbox, Empty, Form, Input, Modal, Select, Space, Table, Tag, Typography, message } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import TagsInput from '../../../shared/components/TagsInput';
import { QUIET } from './api';
import { useTranslation } from '../../../shared/i18n';
import { fmtDate, listOf, money, sitePath, toastErr, totalOf, useSite } from './common';

const { Text } = Typography;

const AddCustomerModal = ({ open, onClose, onDone }) => {
    const { t } = useTranslation();
    const { api } = useSite();
    const [form] = Form.useForm();
    const [busy, setBusy] = useState(false);
    const submit = async () => {
        const v = await form.validateFields();
        setBusy(true);
        try { const c = await api.post('/customers', { ...v, tags: v.tags || [] }); message.success(t('Customer added')); onDone(c); } catch (e) { toastErr(e); } finally { setBusy(false); }
    };
    return (
        <Modal open={open} title={t('Add customer')} onCancel={onClose} onOk={submit} confirmLoading={busy} destroyOnClose afterOpenChange={(o) => o && form.resetFields()}>
            <Form form={form} layout="vertical" preserve={false}>
                <Form.Item name="email" label={t('Email', { context: 'shop' })} rules={[{ required: true, type: 'email' }]}><Input /></Form.Item>
                <Form.Item name="name" label={t('Name')}><Input maxLength={191} /></Form.Item>
                <Form.Item name="phone" label={t('Phone')}><Input maxLength={64} /></Form.Item>
                <Form.Item name="tags" label={t('Tags')}><TagsInput format="array" spaceSeparates={false} /></Form.Item>
                <Form.Item name="note" label={t('Notes')}><Input.TextArea autoSize={{ minRows: 2 }} /></Form.Item>
                <Form.Item name="accepts_marketing" valuePropName="checked" style={{ marginBottom: 0 }}><Checkbox>{t('Agreed to receive marketing emails')}</Checkbox></Form.Item>
            </Form>
        </Modal>
    );
};

const Customers = () => {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { api, siteId, currency } = useSite();
    const [f, setF] = useState({ q: '', page: 1, per_page: 25 });
    const [q, setQ] = useState('');
    const [data, setData] = useState({ items: [], total: 0 });
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(0);
    const [tagList, setTagList] = useState([]);
    const [sel, setSel] = useState([]);
    const [adding, setAdding] = useState(false);
    const [bulk, setBulk] = useState(null); // 'add' | 'remove'
    const [bulkTags, setBulkTags] = useState([]);
    useEffect(() => {
        api.get('/customers/tags', null, QUIET).then((d) => setTagList(listOf(d?.tags))).catch(() => {});
    }, [api, reload]);
    const runBulk = async () => {
        try {
            const r = await api.post('/customers/bulk-tags', { ids: sel, add: bulk === 'add' ? bulkTags : [], remove: bulk === 'remove' ? bulkTags : [] });
            message.success(t('{{n}} customers updated', { n: r?.updated ?? sel.length }));
            setBulk(null); setBulkTags([]); setSel([]); setReload((n) => n + 1);
        } catch (e) { toastErr(e); }
    };

    useEffect(() => {
        let live = true;
        setLoading(true);
        api.get('/customers', Object.fromEntries(Object.entries(f).filter(([, v]) => v !== '')))
            .then((d) => live && setData({ items: listOf(d), total: totalOf(d) }))
            .catch((e) => { if (live) { if (e.status !== 404) toastErr(e); setData({ items: [], total: 0 }); } })
            .finally(() => live && setLoading(false));
        return () => { live = false; };
    }, [api, f, reload]);

    return (
        <Card title={t('Customers')} extra={<Space><ExportCustomersButton /><Button type="primary" icon={<PlusOutlined />} onClick={() => setAdding(true)}>{t('Add customer')}</Button></Space>}>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input.Search allowClear placeholder={t('Search name or email')} value={q} onChange={(e) => setQ(e.target.value)}
                    onSearch={(v) => setF((x) => ({ ...x, q: v, page: 1 }))} style={{ width: 280 }} />
                <Select allowClear showSearch placeholder={t('Tag')} value={f.tag || undefined} style={{ width: 180 }} onChange={(tag) => setF((x) => ({ ...x, tag: tag || '', page: 1 }))}
                    options={tagList.map((x) => ({ value: x, label: x }))} />
                <Select value={f.sort || 'newest'} style={{ width: 180 }} onChange={(sort) => setF((x) => ({ ...x, sort, page: 1 }))} options={[
                    { value: 'newest', label: t('Newest first') }, { value: 'oldest', label: t('Oldest first') }, { value: 'spent', label: t('Most spent') },
                    { value: 'orders', label: t('Most orders') }, { value: 'name', label: t('Name A–Z') }, { value: 'last_login', label: t('Last login') },
                ]} />
                {sel.length > 0 && (
                    <Space>
                        <Text type="secondary">{t('{{n}} selected', { n: sel.length })}</Text>
                        <Button size="small" onClick={() => { setBulkTags([]); setBulk('add'); }}>{t('Add tags')}</Button>
                        <Button size="small" onClick={() => { setBulkTags([]); setBulk('remove'); }}>{t('Remove tags')}</Button>
                    </Space>
                )}
            </Space>
            <Table
                rowKey="id"
                rowSelection={{ selectedRowKeys: sel, onChange: setSel, preserveSelectedRowKeys: true }}
                loading={loading}
                dataSource={data.items}
                scroll={{ x: 760 }}
                onRow={(c) => ({ onClick: (e) => { if (!e.target.closest?.('.ant-table-selection-column')) navigate(sitePath(siteId, `customers/${c.id}`)); }, style: { cursor: 'pointer' } })}
                pagination={{ current: f.page, pageSize: f.per_page, total: data.total, hideOnSinglePage: true, onChange: (page, per) => setF((x) => ({ ...x, page, per_page: per })) }}
                columns={[
                    {
                        title: t('Customer'), key: 'n', render: (_, c) => (
                            <div>
                                <a>{c.name || c.email}</a>
                                {c.name && <div><Text type="secondary" style={{ fontSize: 12 }}>{c.email}</Text></div>}
                                {listOf(c.tags).length > 0 && <div>{listOf(c.tags).slice(0, 4).map((x) => <Tag key={x} style={{ fontSize: 11, marginTop: 2 }}>{x}</Tag>)}</div>}
                            </div>
                        ),
                    },
                    { title: t('Account'), key: 'acc', width: 120, render: (_, c) => (c.state === 'disabled' ? <Tag>{t('Disabled')}</Tag> : c.has_account || c.registered ? <Tag color="blue">{t('Registered')}</Tag> : <Tag>{t('Guest')}</Tag>) },
                    { title: t('Marketing'), dataIndex: 'accepts_marketing', width: 110, render: (v) => (v ? <Tag color="green">{t('Subscribed')}</Tag> : <Text type="secondary">-</Text>) },
                    { title: t('Orders'), dataIndex: 'orders_count', width: 90 },
                    { title: t('Amount spent'), dataIndex: 'total_spent', width: 130, align: 'right', render: (v) => money(v || 0, currency) },
                    { title: t('Since', { context: 'shop' }), dataIndex: 'created_at', width: 120, responsive: ['lg'], render: (v) => fmtDate(v, false) },
                ]}
                locale={{ emptyText: <Empty description={f.q || f.tag ? t('No customers match') : t('No customers yet')} /> }}
            />
            <AddCustomerModal open={adding} onClose={() => setAdding(false)} onDone={(c) => { setAdding(false); if (c?.id) navigate(sitePath(siteId, `customers/${c.id}`)); else setReload((n) => n + 1); }} />
            <Modal open={!!bulk} title={bulk === 'remove' ? t('Remove tags') : t('Add tags')} onCancel={() => setBulk(null)} onOk={runBulk} okButtonProps={{ disabled: !bulkTags.length }} destroyOnClose>
                <TagsInput format="array" spaceSeparates={false} value={bulkTags} onChange={setBulkTags} open={undefined} suffixIcon={undefined} options={tagList.map((x) => ({ value: x, label: x }))} />
            </Modal>
        </Card>
    );
};

export default Customers;
